import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import CreateProjectModal from './CreateProjectModal';
import KanbanBoard from './KanbanBoard';
import LoadingSpinner from './LoadingSpinner';
import { OfficeVirtualModal } from './OfficeVirtual';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ProjectsPage.css';
import AppIcon from './icons/AppIcon';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active'); // Padrão: apenas ativos
  const [selectedProject, setSelectedProject] = useState(null);

  const [users, setUsers] = useState([]);

  // Estados para o Escritório Virtual
  const [showOfficeVirtual, setShowOfficeVirtual] = useState(false);
  const [officeVirtualProject, setOfficeVirtualProject] = useState(null);
  const [officeVirtualConfig, setOfficeVirtualConfig] = useState(null);

  // Configurar sensores de drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Carregar projetos e usuários
  useEffect(() => {
    loadProjects();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email')
        .eq('company_id', user.company_id)
        .order('nome');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      if (!user?.company_id) {
        console.warn('⚠️ Usuário sem company_id');
        setProjects([]);
        return;
      }

      // Carregar projetos do Supabase ordenados por display_order
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('company_id', user.company_id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      // Para cada projeto, buscar contagem de listas e itens
      const projectsWithStats = await Promise.all((data || []).map(async (project) => {
        // Contar listas
        const { count: listsCount } = await supabase
          .from('project_lists')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', project.id);
        
        // Contar itens
        const { count: itemsCount } = await supabase
          .from('project_items')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', project.id);
        
        return {
          ...project,
          lists_count: listsCount || 0,
          items_count: itemsCount || 0
        };
      }));
      
      setProjects(projectsWithStats);
      // console.log('✅ Projetos carregados do Supabase:', projectsWithStats?.length || 0);
    } catch (error) {
      console.error('❌ Erro ao carregar projetos:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Criar novo projeto no Supabase
  const handleCreateProject = async (projectData) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const projectType = String(projectData?.project_type || '').trim();
      
      if (!user?.company_id || !user?.id) {
        alert('Erro: Usuário não autenticado corretamente');
        return;
      }

      // Preparar membros no formato JSONB
      let membersArray = [];
      if (!projectData.allUsersSelected && projectData.members?.length > 0) {
        membersArray = projectData.members.map(userId => ({
          user_id: userId,
          role: 'member'
        }));
      }

      // Preparar settings com configurações CRM se aplicável
      const settings = {
        allow_comments: true,
        allow_attachments: true,
        allow_subtasks: true,
        require_due_date: false,
        auto_archive_completed: false,
        notifications_enabled: true
      };

      // Se for CRM, adicionar configurações específicas
      if (projectType === 'crm') {
        settings.crm = {
          useForm: projectData.useForm || false,
          formFields: projectData.formFields || [],
          connection_id: projectData.connections?.[0] || null, // CRM usa apenas 1 conexão
          agent_ids: projectData.agents || []
        };
      }

      // Se for CRM Multi, adicionar configurações específicas (múltiplas conexões)
      if (projectType === 'crm_multi') {
        settings.crm_multi = {
          useForm: projectData.useForm || false,
          formFields: projectData.formFields || [],
          connection_ids: projectData.connections || [], // CRM Multi usa array de conexões
          agent_ids: projectData.agents || []
        };
      }

      if (projectType === 'bar_restaurant') {
        settings.bar_restaurant = {
          subtype: projectData.barRestaurantProjectType || 'service_management',
          connection_id: projectData.connections?.[0] || null,
        };
      }

      if (projectType === 'marketing_campaign') {
        settings.marketing_campaign = {
          dynamic_config_pending: true,
          connection_ids: Array.isArray(projectData.connections) ? projectData.connections : [],
          agent_ids: Array.isArray(projectData.agents) ? projectData.agents : [],
        };
      }

      // Calcular próximo display_order
      const maxOrder = projects.length > 0 
        ? Math.max(...projects.map(p => p.display_order || 0)) 
        : -1;

      // Criar projeto no Supabase
      const newProject = {
        company_id: user.company_id,
        name: projectData.name,
        description: projectData.description || null,
        project_type: projectType || 'custom',
        icon: projectData.icon,
        color: projectData.color,
        background_image: projectData.backgroundImageUrl || null,
        owner_id: user.id,
        created_by: user.id,
        is_archived: false,
        members: membersArray,
        settings: settings,
        display_order: maxOrder + 1,
        // Se tiver agentes, vincular o primeiro como principal
        ai_agent_id: projectData.agents?.[0] || null
      };

      const { data, error } = await supabase
        .from('projects')
        .insert([newProject])
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Projeto criado no Supabase:', data.id);

      // Criar listas baseadas no tipo de projeto
      if (projectType === 'crm' || projectType === 'crm_multi') {
        // Listas CRM (mesmas para CRM e CRM Multi)
        const crmLists = [
          { name: 'Leads Recebidos', description: 'Novos contatos do WhatsApp', color: '#3b82f6', icon: 'inbox', order: 0 },
          { name: 'Em Atendimento', description: 'Leads sendo atendidos', color: '#f59e0b', icon: 'support_agent', order: 1 }
        ];

        // Adicionar lista de formulário se ativado
        if (projectData.useForm) {
          crmLists.push({ 
            name: 'Aguardando Formulário', 
            description: 'Leads com formulário pendente', 
            color: '#8b5cf6', 
            icon: 'assignment', 
            order: 2 
          });
        }

        // Adicionar listas restantes
        crmLists.push(
          { name: 'Equipe de Vendas', description: projectData.useForm ? 'Leads qualificados' : 'Prontos para venda', color: '#10b981', icon: 'trending_up', order: projectData.useForm ? 3 : 2 },
          { name: 'Vendas Efetivadas', description: 'Vendas concluídas', color: '#06b6d4', icon: 'check_circle', order: projectData.useForm ? 4 : 3 },
          { name: 'Follow-up 1', description: 'Primeiro acompanhamento', color: '#ec4899', icon: 'schedule', order: projectData.useForm ? 5 : 4 },
          { name: 'Follow-up 2', description: 'Segundo acompanhamento', color: '#f472b6', icon: 'schedule', order: projectData.useForm ? 6 : 5 },
          { name: 'Follow-up 3', description: 'Terceiro acompanhamento', color: '#fb7185', icon: 'schedule', order: projectData.useForm ? 7 : 6 }
        );

        console.log('📋 Tentando criar listas CRM:', {
          projectId: data.id,
          companyId: user.company_id,
          listsCount: crmLists.length,
          lists: crmLists.map(l => l.name)
        });

        const { error: listsError, data: listsData } = await supabase
          .from('project_lists')
          .insert(crmLists.map(list => ({
            project_id: data.id,
            company_id: user.company_id,
            name: list.name,
            description: list.description,
            color: list.color,
            icon: list.icon,
            display_order: list.order,
            created_by: user.id
          })));

        if (listsError) {
          console.error('❌ Erro ao criar listas CRM:', listsError);
          alert('⚠️ Projeto criado, mas houve erro ao criar as listas: ' + listsError.message);
        } else {
          console.log(`✅ ${crmLists.length} listas CRM criadas automaticamente`, listsData);
        }
      } else if (projectType === 'marketing_campaign') {
        const campaignLists = [
          { name: 'Leads Recebidos', description: 'Novos contatos recebidos', color: '#3b82f6', icon: 'inbox', order: 0 },
          { name: 'Disparo pendente', description: 'Aguardando disparo', color: '#f97316', icon: 'schedule_send', order: 1 },
          { name: 'Disparo efetivado', description: 'Disparo realizado', color: '#22c55e', icon: 'send', order: 2 },
          { name: 'Em Atendimento', description: 'Leads sendo atendidos', color: '#f59e0b', icon: 'support_agent', order: 3 },
          { name: 'Equipe de Vendas', description: 'Prontos para venda', color: '#10b981', icon: 'trending_up', order: 4 },
          { name: 'Vendas Efetivadas', description: 'Vendas concluídas', color: '#06b6d4', icon: 'check_circle', order: 5 },
          { name: 'Follow-up 1', description: 'Primeiro acompanhamento', color: '#ec4899', icon: 'schedule', order: 6 },
          { name: 'Follow-up 2', description: 'Segundo acompanhamento', color: '#f472b6', icon: 'schedule', order: 7 },
          { name: 'Follow-up 3', description: 'Terceiro acompanhamento', color: '#fb7185', icon: 'schedule', order: 8 },
        ];

        const { error: campaignListsError, data: campaignListsData } = await supabase
          .from('project_lists')
          .insert(campaignLists.map((list) => ({
            project_id: data.id,
            company_id: user.company_id,
            name: list.name,
            description: list.description,
            color: list.color,
            icon: list.icon,
            display_order: list.order,
            created_by: user.id,
          })));

        if (campaignListsError) {
          console.error('❌ Erro ao criar listas Campanha de marketing:', campaignListsError);
          alert('⚠️ Projeto criado, mas houve erro ao criar as listas: ' + campaignListsError.message);
        } else {
          console.log(`✅ ${campaignLists.length} listas Campanha de marketing criadas automaticamente`, campaignListsData);
        }
      } else if (projectType === 'bar_restaurant') {
        const brLists = [
          { name: 'Mesas em aberto', description: 'Mesas atendendo hoje', color: '#10b981', icon: 'table_restaurant', order: 0 },
          { name: 'Mesas fechadas', description: 'Mesas finalizadas', color: '#6b7280', icon: 'event_available', order: 1 },
          { name: 'Pedidos em aberto', description: 'Aguardando produção', color: '#3b82f6', icon: 'receipt_long', order: 2 },
          { name: 'Em produção', description: 'Cozinha/Bar preparando', color: '#f59e0b', icon: 'restaurant', order: 3 },
          { name: 'Pronto', description: 'Pronto para entrega', color: '#22c55e', icon: 'done_all', order: 4 },
          { name: 'Entregue', description: 'Pedido entregue ao cliente', color: '#06b6d4', icon: 'local_shipping', order: 5 },
        ];

        const { error: brListsError, data: brListsData } = await supabase
          .from('project_lists')
          .insert(brLists.map(list => ({
            project_id: data.id,
            company_id: user.company_id,
            name: list.name,
            description: list.description,
            color: list.color,
            icon: list.icon,
            display_order: list.order,
            created_by: user.id
          })));

        if (brListsError) {
          console.error('❌ Erro ao criar listas Bar & Restaurante:', brListsError);
          alert('⚠️ Projeto criado, mas houve erro ao criar as listas: ' + brListsError.message);
        } else {
          console.log(`✅ ${brLists.length} listas Bar & Restaurante criadas automaticamente`, brListsData);
        }

        try {
          const connectionId = projectData.connections?.[0] || null;
          const rawTeam = Array.isArray(projectData.barRestaurantTeam) ? projectData.barRestaurantTeam : [];
          const teamClean = rawTeam
            .map((m) => ({
              name: String(m?.name || '').trim(),
              phone_e164: String(m?.phone || '').replace(/\D/g, ''),
            }))
            .filter((m) => !!m.phone_e164);

          if (!connectionId) {
            console.warn('[bar_restaurant] conexão WhatsApp não selecionada; pulando criação de grupo');
            alert('⚠️ Projeto criado, mas não foi possível criar o grupo: conexão WhatsApp não selecionada.');
          } else {
            if (teamClean.length > 0) {
              const { error: membersErr } = await supabase
                .from('project_whatsapp_members')
                .insert(teamClean.map((m) => ({
                  company_id: user.company_id,
                  project_id: data.id,
                  name: m.name || 'Integrante',
                  phone_e164: m.phone_e164,
                  role: 'member',
                  is_active: true,
                  created_by: user.id,
                })));
              if (membersErr) {
                console.error('❌ Erro ao salvar project_whatsapp_members:', membersErr);
              }
            }

            const groupName = `${projectData.name || 'Projeto'} - Equipe`;
            const participants = teamClean.map((m) => m.phone_e164);
            if (participants.length < 1) {
              console.warn('[bar_restaurant] nenhum participante válido informado; pulando criação de grupo');
              alert('⚠️ Projeto criado, mas não foi possível criar o grupo: nenhum telefone válido foi informado.');
              return;
            }
            const resp = await fetch(`${BACKEND_NODE_URL}/connections/group-create`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ connection_id: connectionId, name: groupName, participants }),
            });

            const payload = await resp.json().catch(() => ({}));
            if (!resp.ok || !payload?.ok) {
              throw new Error(payload?.error || `Falha ao criar grupo (HTTP ${resp.status})`);
            }

            const provider = payload?.provider_response || {};
            const externalGroupId = provider?.group?.JID || null;

            if (externalGroupId) {
              const { error: groupErr } = await supabase
                .from('project_whatsapp_groups')
                .insert([{ 
                  company_id: user.company_id,
                  project_id: data.id,
                  connection_id: connectionId,
                  provider: 'uazapi',
                  group_name: groupName,
                  external_group_id: String(externalGroupId),
                  status: 'active',
                  metadata: { provider_response: provider },
                  created_by: user.id,
                }]);
              if (groupErr) {
                console.error('❌ Erro ao salvar project_whatsapp_groups:', groupErr);
              }

              const currentSettings = data.settings || settings;
              const nextSettings = {
                ...(currentSettings || {}),
                bar_restaurant: {
                  ...((currentSettings || {}).bar_restaurant || {}),
                  staff_group_id: String(externalGroupId),
                  staff_group_name: groupName,
                },
              };
              const { error: upSettingsErr } = await supabase
                .from('projects')
                .update({ settings: nextSettings })
                .eq('id', data.id);
              if (upSettingsErr) {
                console.error('❌ Erro ao atualizar settings do projeto (grupo):', upSettingsErr);
              }
            } else {
              console.warn('[bar_restaurant] grupo criado mas external_group_id não identificado; salvando apenas metadata em project_whatsapp_groups');
              const { error: groupErr } = await supabase
                .from('project_whatsapp_groups')
                .insert([{ 
                  company_id: user.company_id,
                  project_id: data.id,
                  connection_id: connectionId,
                  provider: 'uazapi',
                  group_name: groupName,
                  external_group_id: `unknown:${Date.now()}`,
                  status: 'active',
                  metadata: { provider_response: provider, note: 'external_group_id not found in response' },
                  created_by: user.id,
                }]);
              if (groupErr) {
                console.error('❌ Erro ao salvar project_whatsapp_groups (fallback):', groupErr);
              }
            }
          }
        } catch (e) {
          console.error('❌ Falha ao criar grupo/salvar integrantes (bar_restaurant):', e);
          alert('⚠️ Projeto criado, mas houve erro ao criar o grupo do WhatsApp: ' + (e?.message || e));
        }
      } else {
        // Lista padrão "Início" para outros tipos
        const { error: listError } = await supabase
          .from('project_lists')
          .insert([{
            project_id: data.id,
            company_id: user.company_id,
            name: 'Início',
            description: 'Lista inicial do projeto',
            display_order: 0,
            color: '#44e1ff',
            icon: 'flag',
            created_by: user.id
          }]);

        if (listError) {
          console.error('⚠️ Erro ao criar lista inicial:', listError);
        } else {
          console.log('✅ Lista "Início" criada automaticamente');
        }
      }

      // Atualizar lista de projetos
      setProjects([data, ...projects]);
      setShowCreateModal(false);
      
      alert('Projeto criado com sucesso!');
      
      // Retornar dados do projeto para que o modal possa usar o ID
      return data;
    } catch (error) {
      console.error('❌ Erro ao criar projeto:', error);
      alert('Erro ao criar projeto: ' + error.message);
      throw error;
    }
  };

  // Filtrar projetos
  const filteredProjects = projects.filter(project => {
    const matchSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || project.project_type === filterType;
    const matchStatus = filterStatus === 'all' || 
                       (filterStatus === 'archived' ? project.is_archived : !project.is_archived);
    
    return matchSearch && matchType && matchStatus;
  });

  // Handler para drag and drop de projetos
  const handleProjectDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = filteredProjects.findIndex(p => p.id === active.id);
    const newIndex = filteredProjects.findIndex(p => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic update - atualizar UI imediatamente
    const reorderedProjects = arrayMove(filteredProjects, oldIndex, newIndex);
    const updatedProjects = reorderedProjects.map((project, index) => ({
      ...project,
      display_order: index
    }));
    
    setProjects(prev => {
      // Manter projetos que não estão no filtro (arquivados, etc)
      const otherProjects = prev.filter(p => 
        !filteredProjects.find(fp => fp.id === p.id)
      );
      return [...otherProjects, ...updatedProjects];
    });

    // Salvar no Supabase em background
    const updates = updatedProjects.map(p => ({
      id: p.id,
      display_order: p.display_order
    }));
    
    Promise.all(
      updates.map(u => 
        supabase
          .from('projects')
          .update({ display_order: u.display_order })
          .eq('id', u.id)
      )
    ).catch(err => console.error('❌ Erro ao salvar ordem dos projetos:', err));
  };

  // Tipos de projeto
  const projectTypes = [
    { value: 'custom', label: 'Personalizado', icon: 'dashboard' },
    { value: 'crm', label: 'CRM', icon: 'people' },
    { value: 'marketing_campaign', label: 'Campanha de marketing', icon: 'campaign' },
    { value: 'crm_multi', label: 'CRM Multi Atendimento', icon: 'groups' },
    { value: 'tasks', label: 'Tarefas', icon: 'task_alt' },
    { value: 'support', label: 'Suporte', icon: 'support_agent' },
    { value: 'bar_restaurant', label: 'Bar & Restaurante', icon: 'restaurant' },
    { value: 'financial', label: 'Financeiro', icon: 'payments' },
    { value: 'hr', label: 'RH', icon: 'badge' },
    { value: 'inventory', label: 'Estoque', icon: 'inventory_2' },
    { value: 'escritorio_virtual', label: 'Escritório Virtual', icon: 'business' }
  ];

  // Função para abrir o Escritório Virtual
  const handleOpenOfficeVirtual = async (project) => {
    try {
      // Buscar configuração do escritório
      const { data: config, error } = await supabase
        .from('office_virtual_config')
        .select('*')
        .eq('project_id', project.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar config:', error);
      }

      setOfficeVirtualProject(project);
      setOfficeVirtualConfig(config || {});
      setShowOfficeVirtual(true);
    } catch (err) {
      console.error('Erro ao abrir escritório virtual:', err);
    }
  };

  // Função para fechar o Escritório Virtual
  const handleCloseOfficeVirtual = () => {
    setShowOfficeVirtual(false);
    setOfficeVirtualProject(null);
    setOfficeVirtualConfig(null);
  };

  // Cores disponíveis
  const availableColors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', 
    '#f59e0b', '#10b981', '#06b6d4', '#3b82f6'
  ];

  // Se um projeto está selecionado, mostrar o Kanban
  if (selectedProject) {
    return (
      <KanbanBoard 
        projectId={selectedProject} 
        onBack={() => setSelectedProject(null)} 
      />
    );
  }

  return (
    <div className="projects-page">
      {/* Header */}
      <div className="projects-header">
        <div className="header-left">
          <h1>
            <AppIcon name="dashboard" />
            Projetos
          </h1>
          <p>Gerencie seus quadros e fluxos de trabalho</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <span className="plus-icon">+</span>
          <span>Novo Projeto</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="projects-filters">
        <div className="search-box">
          <AppIcon name="search" />
          <input
            type="text"
            placeholder="Buscar projetos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="all">Todos os tipos</option>
          {projectTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="archived">Arquivados</option>
        </select>
      </div>

      {/* Grid de Projetos */}
      {loading ? (
        <LoadingSpinner message="Carregando projetos..." />
      ) : filteredProjects.length === 0 ? (
        <div className="empty-state">
          <AppIcon name="dashboard" />
          <h3>Nenhum projeto encontrado</h3>
          <p>Crie seu primeiro projeto para começar</p>
          <button 
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <span className="plus-icon">+</span>
            <span>Novo Projeto</span>
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleProjectDragEnd}
        >
          <SortableContext
            items={filteredProjects.map(p => p.id)}
            strategy={rectSortingStrategy}
          >
            <div className="projects-grid">
              {filteredProjects.map(project => (
                <SortableProjectCard
                  key={project.id}
                  id={project.id}
                  project={project}
                  onRefresh={loadProjects}
                  onOpenProject={setSelectedProject}
                  onOpenOfficeVirtual={handleOpenOfficeVirtual}
                  onEdit={(project) => {
                    setEditingProject(project);
                    setShowEditModal(true);
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Modal Criar Projeto */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateProject}
        users={users}
      />

      {/* Modal Editar Projeto */}
      {showEditModal && editingProject && (
        <CreateProjectModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProject(null);
          }}
          onCreate={async (updatedData) => {
            try {
              const { error } = await supabase
                .from('projects')
                .update({
                  name: updatedData.name,
                  description: updatedData.description,
                  project_type: updatedData.project_type,
                  icon: updatedData.icon,
                  color: updatedData.color,
                  background_image: updatedData.backgroundImageUrl
                })
                .eq('id', editingProject.id);

              if (error) throw error;

              alert('Projeto atualizado com sucesso!');
              setShowEditModal(false);
              setEditingProject(null);
              loadProjects();
            } catch (error) {
              console.error('Erro ao atualizar projeto:', error);
              alert('Erro ao atualizar projeto: ' + error.message);
            }
          }}
          users={users}
          initialData={editingProject}
        />
      )}

      {/* Modal Escritório Virtual */}
      <OfficeVirtualModal
        isOpen={showOfficeVirtual}
        onClose={handleCloseOfficeVirtual}
        projectId={officeVirtualProject?.id}
        officeConfig={officeVirtualConfig}
      />
    </div>
  );
}

// Componente Card Sortable (wrapper com drag and drop)
function SortableProjectCard({ id, project, onRefresh, onOpenProject, onOpenOfficeVirtual, onEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProjectCard
        project={project}
        onRefresh={onRefresh}
        onOpenProject={onOpenProject}
        onOpenOfficeVirtual={onOpenOfficeVirtual}
        onEdit={onEdit}
        isDragging={isDragging}
      />
    </div>
  );
}

// Componente Card do Projeto
function ProjectCard({ project, onRefresh, onOpenProject, onOpenOfficeVirtual, onEdit, isDragging }) {
  const handleOpenProject = () => {
    // Se for escritório virtual, abrir o modal específico
    if (project.project_type === 'escritorio_virtual' && onOpenOfficeVirtual) {
      onOpenOfficeVirtual(project);
    } else {
      onOpenProject(project.id);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(project);
    }
  };

  const handleArchive = async () => {
    try {
      const newArchivedStatus = !project.is_archived;
      
      console.log('🗄️ Arquivando projeto:', project.id, 'Status:', newArchivedStatus);
      
      const { data, error } = await supabase
        .from('projects')
        .update({ is_archived: newArchivedStatus })
        .eq('id', project.id)
        .select();

      if (error) {
        console.error('❌ Erro ao arquivar:', error);
        throw error;
      }
      
      console.log('✅ Projeto atualizado:', data);
      alert(newArchivedStatus ? 'Projeto arquivado!' : 'Projeto desarquivado!');
      onRefresh();
    } catch (error) {
      console.error('❌ Erro ao arquivar projeto:', error);
      alert('Erro ao arquivar projeto: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (!confirm('⚠️ Tem certeza que deseja deletar este projeto? Esta ação não pode ser desfeita!')) {
      return;
    }

    try {
      console.log('🗑️ Deletando projeto:', project.id, 'Tipo:', project.project_type);

      // 1. Deletar registros específicos por tipo de projeto
      if (project.project_type === 'gas_distributor') {
        console.log('🗑️ Deletando registros gas_distributor...');
        
        // Deletar gas_distributor_agents
        const { error: gasAgentsError } = await supabase
          .from('gas_distributor_agents')
          .delete()
          .eq('projeto_id', project.id);
        if (gasAgentsError) console.warn('⚠️ Erro ao deletar gas_distributor_agents:', gasAgentsError);
        
        // Deletar gas_distributor_config
        const { error: gasConfigError } = await supabase
          .from('gas_distributor_config')
          .delete()
          .eq('project_id', project.id);
        if (gasConfigError) console.warn('⚠️ Erro ao deletar gas_distributor_config:', gasConfigError);
        
        // Deletar gas_distributor_products
        const { error: gasProductsError } = await supabase
          .from('gas_distributor_products')
          .delete()
          .eq('project_id', project.id);
        if (gasProductsError) console.warn('⚠️ Erro ao deletar gas_distributor_products:', gasProductsError);
        
        // Deletar gas_distributor_preco_cliente
        const { error: gasPrecoError } = await supabase
          .from('gas_distributor_preco_cliente')
          .delete()
          .eq('project_id', project.id);
        if (gasPrecoError) console.warn('⚠️ Erro ao deletar gas_distributor_preco_cliente:', gasPrecoError);
        
        // Deletar pedido_gas_distributor
        const { error: gasPedidoError } = await supabase
          .from('pedido_gas_distributor')
          .delete()
          .eq('project_id', project.id);
        if (gasPedidoError) console.warn('⚠️ Erro ao deletar pedido_gas_distributor:', gasPedidoError);
        
        console.log('✅ Registros gas_distributor deletados');
      }

      if (project.project_type === 'bar_restaurant') {
        console.log('🗑️ Deletando registros bar_restaurant...');
        
        // Deletar bar_restaurant_tables
        const { error: barTablesError } = await supabase
          .from('bar_restaurant_tables')
          .delete()
          .eq('project_id', project.id);
        if (barTablesError) console.warn('⚠️ Erro ao deletar bar_restaurant_tables:', barTablesError);
        
        // Deletar bar_restaurant_orders
        const { error: barOrdersError } = await supabase
          .from('bar_restaurant_orders')
          .delete()
          .eq('project_id', project.id);
        if (barOrdersError) console.warn('⚠️ Erro ao deletar bar_restaurant_orders:', barOrdersError);
        
        // Deletar bar_restaurant_service_tax
        const { error: barTaxError } = await supabase
          .from('bar_restaurant_service_tax')
          .delete()
          .eq('project_id', project.id);
        if (barTaxError) console.warn('⚠️ Erro ao deletar bar_restaurant_service_tax:', barTaxError);
        
        // Deletar bar_restaurant_turnos
        const { error: barTurnosError } = await supabase
          .from('bar_restaurant_turnos')
          .delete()
          .eq('project_id', project.id);
        if (barTurnosError) console.warn('⚠️ Erro ao deletar bar_restaurant_turnos:', barTurnosError);
        
        // Deletar bar_restaurant_auxiliar_garcon
        const { error: barGarconError } = await supabase
          .from('bar_restaurant_auxiliar_garcon')
          .delete()
          .eq('projeto_id', project.id);
        if (barGarconError) console.warn('⚠️ Erro ao deletar bar_restaurant_auxiliar_garcon:', barGarconError);
        
        // Deletar bar_restaurant_gerente_salao
        const { error: barGerenteError } = await supabase
          .from('bar_restaurant_gerente_salao')
          .delete()
          .eq('projeto_id', project.id);
        if (barGerenteError) console.warn('⚠️ Erro ao deletar bar_restaurant_gerente_salao:', barGerenteError);
        
        console.log('✅ Registros bar_restaurant deletados');
      }

      if (project.project_type === 'crm') {
        console.log('🗑️ Deletando registros crm...');
        
        // Deletar crm_followup_rule_actions
        const { error: crmActionsError } = await supabase
          .from('crm_followup_rule_actions')
          .delete()
          .eq('project_id', project.id);
        if (crmActionsError) console.warn('⚠️ Erro ao deletar crm_followup_rule_actions:', crmActionsError);
        
        // Deletar crm_followup_rules
        const { error: crmRulesError } = await supabase
          .from('crm_followup_rules')
          .delete()
          .eq('project_id', project.id);
        if (crmRulesError) console.warn('⚠️ Erro ao deletar crm_followup_rules:', crmRulesError);
        
        console.log('✅ Registros crm deletados');
      }

      // 2. Deletar tabelas comuns a todos os projetos
      console.log('🗑️ Deletando tabelas comuns...');
      
      // Deletar project_list_distribution
      const { error: listDistError } = await supabase
        .from('project_list_distribution')
        .delete()
        .eq('project_id', project.id);
      if (listDistError) console.warn('⚠️ Erro ao deletar project_list_distribution:', listDistError);
      
      // Deletar project_list_forward
      const { error: listForwardError } = await supabase
        .from('project_list_forward')
        .delete()
        .eq('project_id', project.id);
      if (listForwardError) console.warn('⚠️ Erro ao deletar project_list_forward:', listForwardError);
      
      // Deletar calendar_events
      const { error: eventsError } = await supabase
        .from('calendar_events')
        .delete()
        .eq('project_id', project.id);
      if (eventsError) console.warn('⚠️ Erro ao deletar calendar_events:', eventsError);
      
      // Deletar credit_transactions
      const { error: creditError } = await supabase
        .from('credit_transactions')
        .delete()
        .eq('project_id', project.id);
      if (creditError) console.warn('⚠️ Erro ao deletar credit_transactions:', creditError);
      
      // Deletar marketing_contact_lists
      const { error: marketingError } = await supabase
        .from('marketing_contact_lists')
        .delete()
        .eq('project_id', project.id);
      if (marketingError) console.warn('⚠️ Erro ao deletar marketing_contact_lists:', marketingError);
      
      // Deletar remarketing_campaigns
      const { error: remarketingError } = await supabase
        .from('remarketing_campaigns')
        .delete()
        .eq('project_id', project.id);
      if (remarketingError) console.warn('⚠️ Erro ao deletar remarketing_campaigns:', remarketingError);

      // 3. Buscar IDs dos itens e listas para deletar tabelas dependentes
      console.log('🗑️ Buscando IDs dos itens e listas...');
      
      // Buscar IDs dos project_items
      const { data: itemsData } = await supabase
        .from('project_items')
        .select('id')
        .eq('project_id', project.id);
      const itemIds = (itemsData || []).map(item => item.id);
      
      // Buscar IDs das project_lists
      const { data: listsData } = await supabase
        .from('project_lists')
        .select('id')
        .eq('project_id', project.id);
      const listIds = (listsData || []).map(list => list.id);
      
      console.log(`📋 Encontrados ${itemIds.length} itens e ${listIds.length} listas`);

      // 4. Deletar tabelas que dependem de project_items
      if (itemIds.length > 0) {
        console.log('🗑️ Deletando tabelas dependentes de project_items...');
        
        // Deletar item_comments
        const { error: commentsError } = await supabase
          .from('item_comments')
          .delete()
          .in('item_id', itemIds);
        if (commentsError) console.warn('⚠️ Erro ao deletar item_comments:', commentsError);
        
        // Deletar item_history
        const { error: historyError } = await supabase
          .from('item_history')
          .delete()
          .in('item_id', itemIds);
        if (historyError) console.warn('⚠️ Erro ao deletar item_history:', historyError);
        
        // Deletar item_tags
        const { error: tagsError } = await supabase
          .from('item_tags')
          .delete()
          .in('item_id', itemIds);
        if (tagsError) console.warn('⚠️ Erro ao deletar item_tags:', tagsError);
        
        // Deletar project_list_forward_jobs
        const { error: forwardJobsError } = await supabase
          .from('project_list_forward_jobs')
          .delete()
          .in('item_id', itemIds);
        if (forwardJobsError) console.warn('⚠️ Erro ao deletar project_list_forward_jobs:', forwardJobsError);
        
        console.log('✅ Tabelas dependentes de project_items deletadas');
      }

      // 5. Deletar todos os itens do projeto
      const { error: itemsError } = await supabase
        .from('project_items')
        .delete()
        .eq('project_id', project.id);

      if (itemsError) {
        console.error('❌ Erro ao deletar itens:', itemsError);
        throw itemsError;
      }

      // 6. Deletar todas as listas do projeto
      const { error: listsError } = await supabase
        .from('project_lists')
        .delete()
        .eq('project_id', project.id);

      if (listsError) {
        console.error('❌ Erro ao deletar listas:', listsError);
        throw listsError;
      }

      // 5. Deletar o projeto
      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id);

      if (projectError) {
        console.error('❌ Erro ao deletar projeto:', projectError);
        throw projectError;
      }

      console.log('✅ Projeto deletado com sucesso');
      alert('Projeto deletado com sucesso!');
      onRefresh();
    } catch (error) {
      console.error('❌ Erro ao deletar projeto:', error);
      alert('Erro ao deletar projeto: ' + error.message);
    }
  };

  const getProjectTypeLabel = (type) => {
    const types = {
      custom: 'Personalizado',
      crm: 'CRM',
      marketing_campaign: 'Campanha de marketing',
      tasks: 'Tarefas',
      support: 'Suporte',
      sales: 'Vendas',
      financial: 'Financeiro',
      hr: 'RH',
      inventory: 'Estoque'
    };
    return types[type] || type;
  };

  return (
    <div className={`project-card ${isDragging ? 'dragging' : ''}`}>
      {/* Imagem/Background do Projeto - clicável */}
      <div 
        className="project-image project-image-clickable" 
        style={{ backgroundColor: project.color }}
        onClick={handleOpenProject}
        title="Clique para abrir o projeto"
      >
        {project.background_image ? (
          <img src={project.background_image} alt={project.name} />
        ) : (
          <div className="no-image">
            <AppIcon name={project.icon} />
          </div>
        )}
        {project.is_archived && <span className="badge badge-archived">Arquivado</span>}
      </div>
      
      {/* Informações do Projeto */}
      <div className="project-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>{project.name}</h3>
          <span className={`badge-status ${project.is_archived ? 'archived' : 'active'}`}>
            {project.is_archived ? 'Arquivado' : 'Ativo'}
          </span>
          <span className="project-type-icon" title={getProjectTypeLabel(project.project_type)}>
            <AppIcon name={project.icon} />
          </span>
        </div>
        <p className="project-description">
          {project.description || 'Sem descrição'}
        </p>
        
        <div className="project-stats">
          <div className="stat-item">
            <AppIcon name="view_week" />
            <span>{project.lists_count || 0} {project.lists_count === 1 ? 'lista' : 'listas'}</span>
          </div>
          <div className="stat-item">
            <AppIcon name="task" />
            <span>{project.items_count || 0} {project.items_count === 1 ? 'item' : 'itens'}</span>
          </div>
        </div>
      </div>
      
      {/* Ações do Projeto */}
      <div className="project-actions">
        <button className="btn-icon" onClick={handleOpenProject} title="Abrir projeto">
          <AppIcon name="open_in_new" />
        </button>
        <button className="btn-icon" onClick={handleEdit} title="Editar projeto">
          <AppIcon name="edit" />
        </button>
        <button className="btn-icon" onClick={handleArchive} title={project.is_archived ? 'Desarquivar' : 'Arquivar'}>
          <AppIcon name={project.is_archived ? 'unarchive' : 'archive'} />
        </button>
        <button className="btn-icon btn-delete" onClick={handleDelete} title="Deletar projeto">
          <AppIcon name="delete" />
        </button>
      </div>
    </div>
  );
}
